import React from 'react';
import { trackError, trackEvent } from '../lib/analytics';
import { captureException, addBreadcrumb } from '../lib/sentry';

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs them, and displays a fallback UI instead of crashing the app.
 *
 * Features:
 * - Logs errors to console
 * - Tracks errors in Google Analytics
 * - Reports errors to Sentry (if configured)
 * - Provides user-friendly fallback UI
 * - Auto-redirects to homepage after 30 seconds to prevent users getting stuck
 * - Clear Cache & Retry button for chunk load errors
 * - Progressive error disclosure
 * - Memory leak prevention with unmount detection
 * - Offline detection before recovery attempts (HP-001)
 * - Error recovery success tracking (HP-002)
 * - Navigation reset on browser back/forward (HP-003)
 * - Login button for auth errors (MP-001)
 * - Service Worker update detection (MP-002)
 * - Error frequency throttling (MP-003)
 * - Full ARIA accessibility support (A11Y-001)
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  // MP-003: Static cache for error throttling (shared across all instances)
  static errorCache = new Map();
  static THROTTLE_MS = 60000; // 60 seconds
  static CACHE_CLEANUP_MS = 300000; // 5 minutes

  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      countdown: 30,
      retryCount: 0,
      showDetails: false,
      errorType: null, // 'chunk', 'network', 'auth', 'unknown'
    };
    this.redirectTimer = null;
    this.countdownInterval = null;
    this.isUnmounted = false;
    this.isRedirecting = false;
    
    // Unique IDs for ARIA relationships
    this.errorTitleId = 'error-boundary-title';
    this.errorDescId = 'error-boundary-description';
    this.errorDetailsId = 'error-boundary-details';
  }

  static getDerivedStateFromError(error) {
    // Classify the error type
    const errorType = ErrorBoundary.classifyError(error);
    return { hasError: true, error, errorType };
  }

  static classifyError(error) {
    const message = error?.message?.toLowerCase() || '';
    const name = error?.name || '';

    // Chunk load / lazy load errors
    if (
      name === 'ChunkLoadError' ||
      message.includes('loading chunk') ||
      message.includes('loading css chunk') ||
      message.includes('dynamically imported module')
    ) {
      return 'chunk';
    }

    // Network errors
    if (
      message.includes('failed to fetch') ||
      message.includes('network') ||
      message.includes('timeout') ||
      name === 'NetworkError'
    ) {
      return 'network';
    }

    // Auth errors
    if (
      message.includes('unauthorized') ||
      message.includes('401') ||
      message.includes('session') ||
      message.includes('token')
    ) {
      return 'auth';
    }

    return 'unknown';
  }

  // MP-003: Clean up old error cache entries
  static cleanupErrorCache() {
    const now = Date.now();
    for (const [key, timestamp] of ErrorBoundary.errorCache.entries()) {
      if (now - timestamp > ErrorBoundary.CACHE_CLEANUP_MS) {
        ErrorBoundary.errorCache.delete(key);
      }
    }
  }

  // MP-003: Check if error should be logged (throttling)
  shouldLogError(error) {
    const errorKey = `${error?.name || 'Error'}:${error?.message || 'unknown'}`;
    const lastOccurrence = ErrorBoundary.errorCache.get(errorKey);
    const now = Date.now();

    // Cleanup old entries periodically
    if (ErrorBoundary.errorCache.size > 50) {
      ErrorBoundary.cleanupErrorCache();
    }

    if (lastOccurrence && now - lastOccurrence < ErrorBoundary.THROTTLE_MS) {
      console.debug('[ErrorBoundary] Throttling duplicate error:', errorKey);
      return false;
    }

    ErrorBoundary.errorCache.set(errorKey, now);
    return true;
  }

  componentDidMount() {
    // HP-003: Listen for browser navigation (back/forward)
    window.addEventListener('popstate', this.handlePopState);
  }

  componentDidCatch(error, errorInfo) {
    if (this.isUnmounted) return;

    // Log the error
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Store error info for display
    this.setState({ errorInfo });

    // MP-003: Only log to external services if not throttled
    if (this.shouldLogError(error)) {
      // Track error in analytics with type
      try {
        trackError(
          'react_error',
          error?.message || 'Unknown error',
          errorInfo?.componentStack?.split('\n')[1]?.trim() || 'Unknown location'
        );
      } catch (analyticsError) {
        console.warn('Failed to track error:', analyticsError);
      }

      // Report to Sentry with full context
      try {
        captureException(error, {
          componentStack: errorInfo?.componentStack,
          errorBoundary: true,
          errorType: this.state.errorType,
          url: window.location.href,
          retryCount: this.state.retryCount,
        });
      } catch (sentryError) {
        console.warn('Failed to report to Sentry:', sentryError);
      }

      // Add breadcrumb for debugging
      try {
        addBreadcrumb('Error boundary triggered', 'error', {
          errorMessage: error?.message,
          errorName: error?.name,
          errorType: this.state.errorType,
        });
      } catch (breadcrumbError) {
        console.warn('Failed to add breadcrumb:', breadcrumbError);
      }
    }

    // Start auto-redirect timer
    this.startAutoRedirect();
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.clearTimers();
    // HP-003: Remove navigation listener
    window.removeEventListener('popstate', this.handlePopState);
  }

  // HP-003: Handle browser back/forward navigation
  handlePopState = () => {
    if (this.state.hasError && !this.isUnmounted) {
      this.clearTimers();
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        countdown: 30,
        retryCount: 0,
        showDetails: false,
        errorType: null,
      });
    }
  };

  clearTimers = () => {
    if (this.redirectTimer) {
      clearTimeout(this.redirectTimer);
      this.redirectTimer = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  };

  safeSetState = (updater) => {
    if (!this.isUnmounted) {
      this.setState(updater);
    }
  };

  // HP-002: Store recovery attempt before reload
  storeRecoveryPending = (method) => {
    try {
      sessionStorage.setItem('error_recovery_pending', JSON.stringify({
        errorType: this.state.errorType,
        method: method,
        timestamp: Date.now()
      }));
    } catch (e) {
      // Ignore storage errors
    }
  };

  startAutoRedirect = () => {
    // Clear any existing timers first
    this.clearTimers();

    // Countdown every second
    this.countdownInterval = setInterval(() => {
      if (this.isUnmounted || this.isRedirecting) {
        this.clearTimers();
        return;
      }
      this.safeSetState(prev => ({
        countdown: Math.max(0, prev.countdown - 1)
      }));
    }, 1000);

    // Redirect after 30 seconds
    this.redirectTimer = setTimeout(() => {
      if (this.isUnmounted || this.isRedirecting) return;
      this.isRedirecting = true;
      this.clearTimers();
      
      // Track auto-redirect
      try {
        trackEvent('error_recovery', {
          action: 'auto_redirect',
          error_type: this.state.errorType,
        });
      } catch (e) {
        // Ignore tracking errors
      }

      // HP-002: Store recovery pending
      this.storeRecoveryPending('auto_redirect');
      
      window.location.replace('/');
    }, 30000);
  };

  handleReload = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this.isRedirecting) return;

    // HP-001: Check if offline
    if (!navigator.onLine) {
      this.safeSetState({
        errorType: 'network',
      });
      try {
        trackEvent('error_recovery', {
          action: 'offline_detected',
          error_type: this.state.errorType,
        });
      } catch (e) {}
      return;
    }

    this.isRedirecting = true;
    this.clearTimers();

    // Track reload attempt
    try {
      trackEvent('error_recovery', {
        action: 'reload',
        error_type: this.state.errorType,
      });
    } catch (err) {
      // Ignore tracking errors
    }

    // HP-002: Store recovery pending
    this.storeRecoveryPending('reload');

    try {
      window.location.reload();
    } catch (error) {
      console.error('Failed to reload:', error);
      window.location.replace(window.location.href);
    }
  };

  handleClearCacheAndRetry = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this.isRedirecting) return;

    // HP-001: Check if offline
    if (!navigator.onLine) {
      this.safeSetState({
        errorType: 'network',
      });
      try {
        trackEvent('error_recovery', {
          action: 'offline_detected',
          error_type: this.state.errorType,
        });
      } catch (e) {}
      return;
    }
    
    this.clearTimers();

    // Track clear cache attempt
    try {
      trackEvent('error_recovery', {
        action: 'clear_cache',
        error_type: this.state.errorType,
      });
    } catch (err) {
      // Ignore tracking errors
    }

    try {
      // Clear application caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      // MP-002: Check for service worker updates
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          await registration.update();

          // If there's a waiting worker, skip waiting
          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            // Small delay to let SW activate
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (swError) {
          console.warn('Service worker update failed:', swError);
        }
      }

      // Clear localStorage entries that might be stale
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('chunk') || key.includes('cache'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // HP-002: Store recovery pending
      this.storeRecoveryPending('clear_cache');

      // Force reload bypassing cache
      this.isRedirecting = true;
      window.location.reload(true);
    } catch (error) {
      console.error('Failed to clear cache:', error);
      // HP-002: Store recovery pending
      this.storeRecoveryPending('clear_cache');
      // Fallback to regular reload
      this.isRedirecting = true;
      window.location.reload();
    }
  };

  handleGoHome = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this.isRedirecting) return;
    this.isRedirecting = true;
    this.clearTimers();

    // Track go home
    try {
      trackEvent('error_recovery', {
        action: 'go_home',
        error_type: this.state.errorType,
      });
    } catch (err) {
      // Ignore tracking errors
    }

    // HP-002: Store recovery pending
    this.storeRecoveryPending('go_home');

    try {
      window.location.replace('/');
    } catch (error) {
      console.error('Failed to navigate home:', error);
      window.location.href = '/';
    }
  };

  // MP-001: Handle login for auth errors
  handleLogin = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this.isRedirecting) return;
    this.isRedirecting = true;
    this.clearTimers();

    try {
      trackEvent('error_recovery', {
        action: 'login',
        error_type: this.state.errorType,
      });
    } catch (err) {}

    // Clear auth state and redirect to login
    try {
      localStorage.removeItem('auth_token');
      sessionStorage.clear();
      window.location.replace('/login');
    } catch (error) {
      window.location.href = '/login';
    }
  };

  toggleDetails = () => {
    this.safeSetState(prev => ({ showDetails: !prev.showDetails }));
  };

  getErrorMessage = () => {
    const { errorType, retryCount } = this.state;

    switch (errorType) {
      case 'chunk':
        return {
          title: 'Failed to load page content',
          description: retryCount > 0
            ? 'We\'re still having trouble loading. Try clearing your cache or check your internet connection.'
            : 'This usually happens after an update. Try refreshing the page.',
          showClearCache: true,
          showLoginButton: false,
        };
      case 'network':
        return {
          title: navigator.onLine ? 'Connection problem' : 'You\'re offline',
          description: navigator.onLine 
            ? 'Please check your internet connection and try again.'
            : 'Please connect to the internet to continue.',
          showClearCache: false,
          showLoginButton: false,
        };
      case 'auth':
        return {
          title: 'Session expired',
          description: 'Your session has expired. Please log in again.',
          showClearCache: false,
          showLoginButton: true, // MP-001
        };
      default:
        return {
          title: 'Something went wrong',
          description: 'We\'re sorry, but something unexpected happened. Our team has been notified.',
          showClearCache: retryCount > 0,
          showLoginButton: false,
        };
    }
  };

  // A11Y-001: Get error type label for screen readers
  getErrorTypeLabel = () => {
    const { errorType } = this.state;
    switch (errorType) {
      case 'chunk':
        return 'Loading Error';
      case 'network':
        return navigator.onLine ? 'Network Error' : 'Offline';
      case 'auth':
        return 'Authentication Error';
      default:
        return 'Application Error';
    }
  };

  render() {
    if (this.state.hasError) {
      const { title, description, showClearCache, showLoginButton } = this.getErrorMessage();
      const { showDetails, countdown, errorType } = this.state;
      const errorTypeLabel = this.getErrorTypeLabel();

      return (
        <div 
          className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center px-4"
          role="main"
          aria-labelledby={this.errorTitleId}
        >
          {/* A11Y-001: Live region for screen reader announcements */}
          <div 
            role="alert" 
            aria-live="assertive" 
            aria-atomic="true"
            className="sr-only"
          >
            {`${errorTypeLabel}: ${title}. ${description}`}
          </div>

          <div className="max-w-lg w-full text-center">
            {/* Error Icon - hidden from screen readers as decorative */}
            <div className="mb-6" aria-hidden="true">
              <svg
                className="w-24 h-24 mx-auto text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Error Message */}
            <h1 
              id={this.errorTitleId}
              className="text-3xl font-bold text-gray-800 mb-4"
            >
              {title}
            </h1>
            <p 
              id={this.errorDescId}
              className="text-gray-600 mb-6"
            >
              {description}
            </p>

            {/* Error Type Badge */}
            {errorType && errorType !== 'unknown' && (
              <div className="mb-4">
                <span 
                  className="inline-block px-3 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full"
                  role="status"
                  aria-label={`Error type: ${errorTypeLabel}`}
                >
                  {errorTypeLabel}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              role="group"
              aria-label="Error recovery options"
            >
              <button
                type="button"
                onClick={this.handleReload}
                aria-label="Try again by reloading the page"
                aria-describedby={this.errorDescId}
                className="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors shadow-lg cursor-pointer"
              >
                Try Again
              </button>
              
              {showClearCache && (
                <button
                  type="button"
                  onClick={this.handleClearCacheAndRetry}
                  aria-label="Clear browser cache and retry loading"
                  className="px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors shadow-lg cursor-pointer"
                >
                  Clear Cache & Retry
                </button>
              )}

              {/* MP-001: Login button for auth errors */}
              {showLoginButton && (
                <button
                  type="button"
                  onClick={this.handleLogin}
                  aria-label="Log in again to restore your session"
                  className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors shadow-lg cursor-pointer"
                >
                  Log In Again
                </button>
              )}
              
              <button
                type="button"
                onClick={this.handleGoHome}
                aria-label="Navigate to homepage"
                className="px-6 py-3 bg-white text-red-600 font-semibold rounded-lg border-2 border-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors cursor-pointer"
              >
                Go to Homepage
              </button>
            </div>

            {/* Auto-redirect notice */}
            <p 
              className="mt-6 text-sm text-gray-400"
              role="timer"
              aria-live="polite"
              aria-atomic="true"
              aria-label={`Automatic redirect to homepage in ${countdown} seconds`}
            >
              Redirecting to homepage in {countdown} seconds...
            </p>

            {/* Error Details Toggle (Development or after retry) */}
            {(process.env.NODE_ENV === 'development' || this.state.retryCount > 0) && this.state.error && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={this.toggleDetails}
                  aria-expanded={showDetails}
                  aria-controls={this.errorDetailsId}
                  className="text-sm text-gray-500 hover:text-gray-700 underline focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 cursor-pointer"
                >
                  {showDetails ? 'Hide' : 'Show'} technical details
                </button>
                
                {showDetails && (
                  <div 
                    id={this.errorDetailsId}
                    className="mt-4 p-4 bg-red-100 rounded-lg text-left overflow-auto max-h-40"
                    role="region"
                    aria-label="Technical error details"
                  >
                    <p className="text-sm font-mono text-red-800 break-all">
                      {this.state.error.toString()}
                    </p>
                    {this.state.errorInfo && (
                      <pre className="mt-2 text-xs text-red-700 whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Help Text */}
            <p className="mt-4 text-sm text-gray-500">
              If this problem persists, please contact us at{' '}
              <a
                href="mailto:support@startnclex.com"
                className="text-red-600 hover:underline focus:outline-none focus:ring-2 focus:ring-red-500"
                aria-label="Email support at support@startnclex.com"
              >
                support@startnclex.com
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Calendar-specific error boundary with study plan cache clearing
 */
export class CalendarErrorBoundary extends ErrorBoundary {
  static classifyError(error) {
    const message = error?.message?.toLowerCase() || '';

    // Calendar/task-specific errors
    if (
      message.includes('studyunits') ||
      message.includes('task') ||
      message.includes('calendar') ||
      message.includes('schedule')
    ) {
      return 'calendar';
    }

    return ErrorBoundary.classifyError(error);
  }

  handleClearCacheAndRetry = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Clear study plan specific cache
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('simple_study_') ||
        key.startsWith('study_schedule_') ||
        key.includes('progress')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    console.log('[CalendarErrorBoundary] Cleared study plan cache:', keysToRemove.length, 'items');

    // Call parent's clear cache
    await super.handleClearCacheAndRetry(e);
  };

  getErrorMessage = () => {
    if (this.state.errorType === 'calendar') {
      return {
        title: 'Calendar Error',
        description: 'There was a problem loading your study calendar. Clearing the cache should fix this.',
        showClearCache: true,
        showLoginButton: false,
      };
    }
    return super.getErrorMessage();
  };
}

/**
 * Task list error boundary
 */
export class TaskListErrorBoundary extends CalendarErrorBoundary {
  getErrorMessage = () => {
    if (this.state.errorType === 'calendar') {
      return {
        title: 'Task List Error',
        description: 'There was a problem loading your tasks. Try clearing the cache.',
        showClearCache: true,
        showLoginButton: false,
      };
    }
    return super.getErrorMessage();
  };
}

export default ErrorBoundary;
